/**
 * AdminOrganizations — platform admin workspace browser.
 *
 *   - Top stat strip: total orgs, total users (across all orgs), how
 *     many orgs the current admin has opened in this session
 *   - Card grid showing each organization with its brand color,
 *     description, created date, and quick actions (Open, Edit, Delete)
 *   - Right-side slide-out panel for create/edit instead of an inline
 *     form — keeps the grid visible while editing
 *
 * Platform admins can "Open" any org to switch their current org
 * context and dive into that org's library.
 */

import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

import * as api from "../api.js";
import {
  PageHeader,
  Loading,
  ErrorBanner,
  StatCard,
  formatDate,
} from "../components.jsx";
import { useAuth } from "../AuthContext.jsx";

const EMPTY_FORM = {
  slug: "",
  name: "",
  description: "",
  brand_color: "#0f0f0f",
};

export default function AdminOrganizations() {
  const nav = useNavigate();
  const { enterOrg, refreshMe, currentOrg } = useAuth();
  const [orgs, setOrgs] = useState([]);
  const [userCount, setUserCount] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState("");
  const [editing, setEditing] = useState(null); // "new" | slug | null
  const [form, setForm] = useState(EMPTY_FORM);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [orgsRes, usersRes] = await Promise.all([
        api.listOrganizations(),
        api.listAppUsers().catch(() => ({ data: [] })),
      ]);
      setOrgs(orgsRes.data || []);
      setUserCount((usersRes.data || []).length);
      setError(null);
    } catch (err) {
      setError(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return orgs;
    return orgs.filter((o) => {
      const hay = `${o.name} ${o._id} ${o.description || ""}`.toLowerCase();
      return hay.includes(q);
    });
  }, [orgs, search]);

  const startCreate = () => {
    setForm(EMPTY_FORM);
    setEditing("new");
  };

  const startEdit = (org) => {
    setForm({
      slug: org._id,
      name: org.name,
      description: org.description || "",
      brand_color: org.brand_color || "#0f0f0f",
    });
    setEditing(org._id);
  };

  const closePanel = () => {
    setEditing(null);
    setForm(EMPTY_FORM);
  };

  const save = async () => {
    if (!form.name.trim()) {
      setError(new Error("Name is required"));
      return;
    }
    setBusy(true);
    setError(null);
    try {
      if (editing === "new") {
        await api.createOrganization({
          slug: form.slug,
          name: form.name,
          description: form.description,
          brand_color: form.brand_color,
        });
      } else {
        await api.updateOrganization(editing, {
          name: form.name,
          description: form.description,
          brand_color: form.brand_color,
        });
      }
      closePanel();
      await load();
      await refreshMe();
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  };

  const remove = async (org) => {
    if (
      !window.confirm(
        `Delete organization "${org.name}"?\n\nThe org record is removed. Its data (library, tenants) remains in the database but will no longer be reachable through the UI. Use a data cleanup script if you need a full purge.`
      )
    ) {
      return;
    }
    setBusy(true);
    try {
      await api.deleteOrganization(org._id);
      await load();
      await refreshMe();
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  };

  const openOrg = (org) => {
    enterOrg(org._id);
    nav("/"); // dashboard for the newly-entered org
  };

  const totalOrgs = orgs.length;
  const formatCreated = (d) => {
    if (!d) return "—";
    const date = new Date(d);
    return date.toLocaleDateString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  return (
    <>
      <PageHeader
        title="Organizations"
        subtitle="Every company on the Configural platform. Open any workspace to browse its library, switch context, or edit its metadata."
        breadcrumbs={[{ label: "Platform admin" }, { label: "Organizations" }]}
        actions={
          <button className="btn primary" onClick={startCreate}>
            + New organization
          </button>
        }
      />

      <div className="page-body">
        <ErrorBanner error={error} />

        <div className="stat-grid">
          <StatCard
            label="Organizations"
            value={totalOrgs}
            sub={totalOrgs === 1 ? "1 workspace" : `${totalOrgs} workspaces`}
          />
          <StatCard
            label="Users"
            value={userCount ?? "—"}
            sub="Across every organization"
          />
          <StatCard
            label="Current context"
            value={currentOrg?.name || "—"}
            sub={currentOrg?._id || "Pick an org below"}
          />
        </div>

        <div
          className="mb-16"
          style={{ display: "flex", gap: 10, alignItems: "center" }}
        >
          <input
            type="text"
            placeholder="Search organizations..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{
              padding: "8px 12px",
              fontSize: 13,
              border: "1px solid var(--color-border)",
              borderRadius: "var(--radius-sm)",
              minWidth: 280,
            }}
          />
          <span className="muted small">
            {filtered.length} of {orgs.length}
          </span>
        </div>

        {loading ? (
          <Loading />
        ) : filtered.length === 0 ? (
          <div className="empty-state">
            <h3>
              {orgs.length === 0
                ? "No organizations yet"
                : "No organizations match your search"}
            </h3>
            <p>
              {orgs.length === 0
                ? "Click + New organization to create the first workspace."
                : "Try a different search term or clear the filter."}
            </p>
          </div>
        ) : (
          <div className="org-grid">
            {filtered.map((o) => {
              const isCurrent = currentOrg?._id === o._id;
              return (
                <div
                  key={o._id}
                  className={`org-card ${isCurrent ? "current" : ""}`}
                >
                  <div className="org-card-top">
                    <div
                      className="org-card-avatar"
                      style={{ background: o.brand_color || "#0f0f0f" }}
                    >
                      {(o.name || o._id).charAt(0).toUpperCase()}
                    </div>
                    <div className="org-card-heading">
                      <div className="org-card-name">
                        {o.name}
                        {isCurrent && (
                          <span className="badge badge-live" style={{ marginLeft: 8 }}>
                            current
                          </span>
                        )}
                      </div>
                      <div className="org-card-slug mono">{o._id}</div>
                    </div>
                  </div>
                  <p className="org-card-description">
                    {o.description || (
                      <span className="subtle">No description</span>
                    )}
                  </p>
                  <div className="org-card-meta">
                    <span>
                      Created <strong>{formatCreated(o.created_at)}</strong>
                    </span>
                    <span className="org-card-color-chip">
                      <span
                        className="org-card-color-swatch"
                        style={{ background: o.brand_color || "#0f0f0f" }}
                      />
                      {o.brand_color || "#0f0f0f"}
                    </span>
                  </div>
                  <div className="org-card-actions">
                    <button
                      className="btn sm primary"
                      onClick={() => openOrg(o)}
                    >
                      Open workspace
                    </button>
                    <button
                      className="btn sm"
                      onClick={() => startEdit(o)}
                      disabled={busy}
                    >
                      Edit
                    </button>
                    {/* Delete intentionally removed — org deletion is
                        destructive and should only be done via database
                        scripts after confirming all data is backed up. */}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Slide-out create/edit panel */}
      {editing && (
        <SlideOut
          title={
            editing === "new" ? "Create organization" : `Edit ${form.name || editing}`
          }
          subtitle={
            editing === "new"
              ? "Spin up a fresh workspace with its own library, tenants, and branding."
              : "Update the organization's metadata. Slug is immutable."
          }
          onClose={closePanel}
        >
          <div className="form-field">
            <label>Slug</label>
            <input
              type="text"
              value={form.slug}
              onChange={(e) => setForm({ ...form, slug: e.target.value })}
              readOnly={editing !== "new"}
              placeholder="e.g. acme-health"
              style={editing !== "new" ? { background: "#f6f6f6" } : {}}
            />
            <div className="help">
              {editing !== "new"
                ? "The slug is immutable once created."
                : "Used as the org's stable id. Lowercase, no spaces."}
            </div>
          </div>
          <div className="form-field">
            <label>Name</label>
            <input
              type="text"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder="Acme Health"
            />
          </div>
          <div className="form-field">
            <label>Description</label>
            <textarea
              rows={3}
              value={form.description}
              onChange={(e) =>
                setForm({ ...form, description: e.target.value })
              }
              placeholder="What does this organization do?"
            />
          </div>
          <div className="form-field">
            <label>Brand color</label>
            <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
              <input
                type="color"
                value={form.brand_color}
                onChange={(e) =>
                  setForm({ ...form, brand_color: e.target.value })
                }
                style={{
                  width: 56,
                  height: 36,
                  padding: 2,
                  borderRadius: 6,
                }}
              />
              <input
                type="text"
                value={form.brand_color}
                onChange={(e) =>
                  setForm({ ...form, brand_color: e.target.value })
                }
                style={{ flex: 1 }}
              />
            </div>
          </div>

          <div
            className="btn-row"
            style={{ marginTop: 24, justifyContent: "flex-end" }}
          >
            <button className="btn" onClick={closePanel} disabled={busy}>
              Cancel
            </button>
            <button
              className="btn primary"
              onClick={save}
              disabled={busy}
            >
              {busy
                ? "Saving..."
                : editing === "new"
                ? "Create organization"
                : "Save changes"}
            </button>
          </div>
        </SlideOut>
      )}
    </>
  );
}

// ─── Reusable slide-out panel ───────────────────────────────────────
function SlideOut({ title, subtitle, onClose, children }) {
  // Close on Escape
  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose();
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  return (
    <div className="slideout-backdrop" onClick={onClose}>
      <div
        className="slideout-panel"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="slideout-header">
          <div>
            <h2 className="slideout-title">{title}</h2>
            {subtitle && <p className="slideout-subtitle">{subtitle}</p>}
          </div>
          <button
            type="button"
            className="slideout-close"
            onClick={onClose}
            aria-label="Close"
          >
            ×
          </button>
        </div>
        <div className="slideout-body">{children}</div>
      </div>
    </div>
  );
}
